import { z } from 'zod';

import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import { CHAT_UI_MAX_LEGS } from '../../shared/schema/limits.js';
import { chatUiBase } from '../../shared/schema/base.js';
import {
  ChatUiFlightOfferSchema,
  ChatUiFlightSearchEchoSchema,
} from '../flight_offers/flight_offers_schema.js';

/** O trio: cada trecho traz as 3 melhores opções e nada mais. Sem `allOffers` no
 *  multi — 4 trechos × a lista completa estouraria o frame SSE e o `message.ui`
 *  persistido, e o drawer de filtro por trecho ficou fora de escopo. */
export const CHAT_UI_MAX_LEG_OFFERS = 3;

/**
 * UM trecho do roteiro. `origin`/`destination`/`departDate` são EXPLÍCITOS (não
 * derivados de `offers[0]`): é o passo do stepper que desenha rota e data, e o
 * dado do trecho não pode depender de qual voo apareceu nele.
 *
 * A oferta é a MESMA do `flight_offers`, então uma oferta de trecho pode
 * legalmente carregar `returnLeg` — e o renderer do multi o IGNORA: cada trecho é
 * one-way. Não usamos `.omit({ returnLeg: true })` porque forkaria o tipo da
 * oferta e quebraria o reuso do `FlightOfferRow`, tipado em `ChatUiFlightOffer`.
 *
 * `chosen` é ECO do servidor, não a fonte da escolha — o estado vive no
 * componente. Ele chega em dois momentos: na re-emissão depois de uma cota que
 * falhou (a pessoa reescolhe só o trecho que caiu) e no turno de sucesso (o card
 * congelado mostra os tiques em vez de zerar).
 */
const ChatUiMultiLegSchema = z.object({
  origin: z.string().min(1),
  destination: z.string().min(1),
  departDate: z.iso.date(),
  offers: z.array(ChatUiFlightOfferSchema).min(1).max(CHAT_UI_MAX_LEG_OFFERS),
  chosen: z
    .object({
      offerId: z.string().min(1),
      fareId: z.string().min(1),
      price: ChatUiMoneySchema,
    })
    .optional(),
});
export type ChatUiMultiLeg = z.infer<typeof ChatUiMultiLegSchema>;

/**
 * Membro `flight_multi_offers` do `ChatUiSchema`. Um card por ROTEIRO (id estável
 * → reconcilia in-place), com os N trechos já buscados: escolher trecho é local e
 * o único turno sai do CTA final. `challengeId` é obrigatório — sem canal de ação
 * o card não tem função (diferente do `flight_offers`, que tem histórico legado
 * display-only).
 */
export const FlightMultiOffersBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('flight_multi_offers'),
    legs: z.array(ChatUiMultiLegSchema).min(1).max(CHAT_UI_MAX_LEGS),
    challengeId: z.string().min(1),
    // Eco de busca do roteiro SEM o triple por trecho (origin/destination/
    // departDate já vivem em cada `legs[i]`): só o sujeito da política + cabine,
    // que valem pro roteiro inteiro — semente do explorer single-leg por perna.
    search: ChatUiFlightSearchEchoSchema.pick({ cabin: true, policyTravelerId: true }).optional(),
  })
  .strict();

/** Um trecho resolvido no payload do CTA. `legIndex` viaja EXPLÍCITO em vez de
 *  depender da posição no array porque o servidor o valida contra a SUA própria
 *  contagem de trechos — posição concordaria em silêncio com um payload de
 *  tamanho errado. A faixa é refinada no payload INTEIRO (abaixo) e não por-campo,
 *  mas o que o refine alcança é só o PRÓPRIO payload (índices densos 0..n-1): o
 *  schema não vê o roteiro. Conferir contra os trechos do card é do SERVIDOR. */
const MultiLegPickSchema = z
  .object({
    legIndex: z.number().int().min(0),
    offerId: z.string().min(1),
    /** A tarifa escolhida na escada daquele trecho. UM id e não o par
     *  outbound/bundled do `flight_offers`: cada trecho é one-way, e o provider
     *  resolve os dois formatos pelo mesmo `resolveChosen`. */
    fareId: z.string().min(1),
  })
  .strict();

/**
 * O CONJUNTO de picks do board, como o web-chat o anexa ao PRÓXIMO texto livre
 * do composer (`confirmation.selections`) — espelho do `hotel_planner_refine`
 * (#2477). NÃO é um submit: não resolve o challenge (BFF/conversation consultam
 * `isCardContextConfirmation`) e o guard do flight devolve `null` (o texto segue
 * pro mapper); quem o lê é o `map` node, para hidratar `perLeg[i].pick` antes do
 * mapper e o card `:f` da re-busca parcial ecoar `chosen` nas pernas intocadas.
 *
 * ESPARSO por natureza (pick só no trecho 3) e PODE ser vazio (✕ no último
 * pick): por isso não há o refine de faixa `legIndex < legs.length` do CTA — a
 * faixa é conferida no servidor contra `slots.legs.length`. Conjunto inteiro a
 * cada despacho, nunca um delta: undo/✕ chegam como conjunto menor.
 *
 * `blockId` (#6908) é o `ui.id` do card que tem os picks — a IDENTIDADE do card
 * na tela. O servidor o confere contra o card apresentado, e não contra o
 * challenge: o challenge é de uso único (o fence do CTA) e morre com o card
 * ainda vivo na tela. Opcional porque um web-chat anterior não o manda — aí o
 * servidor segue no challenge.
 */
export const FlightMultiLegPicksContextSchema = z
  .object({
    kind: z.literal('flight_multi_leg_picks'),
    blockId: z.string().min(1).optional(),
    legs: z
      .array(MultiLegPickSchema.extend({ price: ChatUiMoneySchema }).strict())
      .max(CHAT_UI_MAX_LEGS),
  })
  .strict()
  .refine(
    (ctx) => new Set(ctx.legs.map((l) => l.legIndex)).size === ctx.legs.length,
    'flight_multi_leg_picks: legIndex repetido',
  );
export type FlightMultiLegPicksContext = z.infer<typeof FlightMultiLegPicksContextSchema>;

/**
 * Payload ecoado em `confirmation.selections` pelo CTA `Confirmar` —
 * discriminado por `action` no submit_guard do flight, como o planner. O roteiro
 * INTEIRO vai junto: um turno cota os N trechos. NUNCA entra no schema de decisões
 * do LLM.
 */
export const FlightMultiLegFareSelectionSchema = z
  .object({
    action: z.literal('select_multi_leg_fares'),
    legs: z.array(MultiLegPickSchema).min(1).max(CHAT_UI_MAX_LEGS),
  })
  .strict()
  .refine(
    (sel) => new Set(sel.legs.map((l) => l.legIndex)).size === sel.legs.length,
    'select_multi_leg_fares: legIndex repetido',
  )
  .refine(
    (sel) => sel.legs.every((l) => l.legIndex < sel.legs.length),
    'select_multi_leg_fares: legIndex fora da faixa do roteiro',
  );
export type FlightMultiLegFareSelection = z.infer<typeof FlightMultiLegFareSelectionSchema>;
